import { useMemo } from 'react';
import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { RiDatabase2Line, RiDownloadLine, RiFileCopyLine } from '@remixicon/react';
import {
  Button,
  CopyButton,
  ExternalLink,
  Flex,
  InfraCodeBlock,
  InfraDivider,
  InfraField,
  InfraSection,
  InfraStack,
  InfraTag,
  InfraTagValue,
  Text,
} from '../ui/InfraPrimitives';

export const GemFireInfoCard = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations || {};

  const templateType = annotations['infrastructure.backstage.io/type'] || '';

  if (templateType !== 'gcp-gemfire') {
    return null;
  }

  const pulseUrl = annotations['gemfire.infrastructure/pulse-url'];
  const pulseUsername = annotations['gemfire.infrastructure/pulse-username'];
  const pulsePassword = annotations['gemfire.infrastructure/pulse-password'];
  const locatorEndpoint = annotations['gemfire.infrastructure/locator-endpoint'];
  const gfshConnectCommand = annotations['gemfire.infrastructure/gfsh-connect-command'];
  const regionInfo = annotations['gemfire.infrastructure/region-info'];
  const accessInstructions = annotations['gemfire.infrastructure/access-instructions'];
  const sshPrivateKeyEncoded = annotations['gemfire.infrastructure/ssh-private-key'];
  const sshCommandsStr = annotations['gemfire.infrastructure/ssh-commands'];
  const consoleUrls = annotations['gemfire.infrastructure/console-urls'];
  const gcloudSshCommands = annotations['gemfire.infrastructure/gcloud-ssh-commands'];

  const gcpProjectId = annotations['gcp.infrastructure/project-id'];
  const gcpRegion = annotations['gcp.infrastructure/region'];
  const gcpZone = annotations['gcp.infrastructure/zone'];
  const gcpNetwork = annotations['gcp.infrastructure/network'];
  const gcpSubnetwork = annotations['gcp.infrastructure/subnetwork'];
  const environmentName = annotations['infrastructure.backstage.io/environment-name'];

  const sshPrivateKey = useMemo(() => {
    if (!sshPrivateKeyEncoded) return '';
    try {
      return window.atob(sshPrivateKeyEncoded);
    } catch (e) {
      console.error('Failed to decode SSH key:', e);
      return '';
    }
  }, [sshPrivateKeyEncoded]);

  const sshCommands = sshCommandsStr?.split('|||').filter(cmd => cmd.trim()) || [];
  const consoleUrlsList = consoleUrls?.split('|||').filter(url => url.trim()) || [];
  const gcloudSshCommandsList = gcloudSshCommands?.split('|||').filter(cmd => cmd.trim()) || [];

  const downloadSSHKey = () => {
    const element = document.createElement('a');
    const file = new Blob([sshPrivateKey], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'ansible_key.pem';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const credentialsText = pulseUsername && pulsePassword
    ? `${pulseUsername}\n${pulsePassword}`
    : '';

  return (
    <InfoCard
      title="GemFire Cluster Information"
      icon={<RiDatabase2Line size={20} />}
    >
      <InfraStack>
        <InfraSection title="☁️ Cluster Configuration">
          {environmentName && (
            <InfraField label="Environment:" value={environmentName} />
          )}
          {locatorEndpoint && (
            <InfraField label="Locator Endpoint:" value={locatorEndpoint} />
          )}
          {gcpRegion && (
            <InfraField
              label="GCP Region:"
              value={<InfraTagValue>{gcpRegion}</InfraTagValue>}
              mono={false}
            />
          )}
          {gcpZone && (
            <InfraField
              label="GCP Zone:"
              value={<InfraTagValue>{gcpZone}</InfraTagValue>}
              mono={false}
            />
          )}
          {gcpNetwork && (
            <InfraField label="VPC Network:" value={gcpNetwork} />
          )}
          {gcpSubnetwork && (
            <InfraField label="Subnetwork:" value={gcpSubnetwork} />
          )}
          {gcpProjectId && (
            <InfraField label="GCP Project:" value={gcpProjectId} />
          )}
        </InfraSection>

        {pulseUrl && (
          <>
            <InfraDivider />
            <InfraSection title="🌐 GemFire Pulse Dashboard">
              <Text variant="title-small">Access URL:</Text>
              <ExternalLink href={pulseUrl}>{pulseUrl}</ExternalLink>

              {pulseUsername && pulsePassword && (
                <>
                  <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
                    Login Credentials:
                  </Text>
                  <InfraCodeBlock variant="light">
                    {`Username: ${pulseUsername}\nPassword: ${pulsePassword}`}
                  </InfraCodeBlock>
                  <Button
                    variant="secondary"
                    size="small"
                    onPress={() => navigator.clipboard.writeText(credentialsText)}
                  >
                    <RiFileCopyLine size={16} />
                    Copy Credentials
                  </Button>
                </>
              )}
            </InfraSection>
          </>
        )}

        {gfshConnectCommand && (
          <>
            <InfraDivider />
            <InfraSection title="🔧 gfsh CLI Access">
              <Text variant="title-small">Connect to cluster:</Text>
              <div style={{ position: 'relative' }}>
                <InfraCodeBlock variant="light">{gfshConnectCommand}</InfraCodeBlock>
                <div style={{ position: 'absolute', top: 4, right: 4 }}>
                  <CopyButton text={gfshConnectCommand} />
                </div>
              </div>
            </InfraSection>
          </>
        )}

        {regionInfo && (
          <>
            <InfraDivider />
            <InfraSection title="📊 Region Information">
              <InfraCodeBlock variant="light">{regionInfo}</InfraCodeBlock>
            </InfraSection>
          </>
        )}

        {(sshPrivateKey || sshCommands.length > 0) && (
          <>
            <InfraDivider />
            <InfraSection title="🔐 SSH Access">
              {sshPrivateKey && (
                <>
                  <Text variant="title-small">SSH Private Key:</Text>
                  <Flex gap="2">
                    <Button variant="primary" size="small" onPress={downloadSSHKey}>
                      <RiDownloadLine size={16} />
                      Download ansible_key.pem
                    </Button>
                    <Button
                      variant="secondary"
                      size="small"
                      onPress={() => navigator.clipboard.writeText(sshPrivateKey)}
                    >
                      <RiFileCopyLine size={16} />
                      Copy to Clipboard
                    </Button>
                  </Flex>
                </>
              )}

              {sshCommands.length > 0 && (
                <>
                  <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
                    SSH Commands:
                  </Text>
                  {sshCommands.map((cmd, index) => (
                    <div key={index} style={{ marginBottom: '0.5rem' }}>
                      <InfraCodeBlock variant="light">{cmd}</InfraCodeBlock>
                    </div>
                  ))}

                  <Text variant="body-small" style={{ marginTop: '0.75rem', display: 'block' }}>
                    💡 <strong>Important:</strong> After downloading, run{' '}
                    <code>chmod 400 ansible_key.pem</code> to fix permissions, then use the SSH
                    commands above.
                  </Text>
                  <Text variant="body-small" style={{ marginTop: '0.5rem', display: 'block' }}>
                    ⚠️ <strong>Without chmod 400:</strong> SSH will reject the key with
                    &quot;UNPROTECTED PRIVATE KEY FILE&quot; error.
                  </Text>
                </>
              )}
            </InfraSection>
          </>
        )}

        {consoleUrlsList.length > 0 && (
          <>
            <InfraDivider />
            <InfraSection title="☁️ GCP Console Access">
              <Text variant="title-small">GCP Console URLs:</Text>
              {consoleUrlsList.map((url, index) => (
                <InfraField
                  key={index}
                  label={`Node ${index + 1}:`}
                  value={
                    <ExternalLink href={url}>Node {index + 1} Console</ExternalLink>
                  }
                  mono={false}
                />
              ))}
            </InfraSection>
          </>
        )}

        {gcloudSshCommandsList.length > 0 && (
          <InfraSection title="gcloud SSH Commands">
            {gcloudSshCommandsList.map((cmd, index) => (
              <div key={index} style={{ marginBottom: '0.5rem' }}>
                <InfraCodeBlock variant="light">{cmd}</InfraCodeBlock>
              </div>
            ))}
            <Text variant="body-small" style={{ marginTop: '0.75rem', display: 'block' }}>
              💡 <strong>Note:</strong> gcloud commands require the Google Cloud SDK to be
              installed and authenticated.
            </Text>
          </InfraSection>
        )}

        {accessInstructions && (
          <>
            <InfraDivider />
            <InfraSection title="🚀 Quick Start">
              <InfraCodeBlock variant="light">{accessInstructions}</InfraCodeBlock>
            </InfraSection>
          </>
        )}
      </InfraStack>
    </InfoCard>
  );
};
